import React from 'react';
import { TESTIMONIALS } from '../constants';
import { Star, ArrowRight, Quote } from 'lucide-react';

type NewsArticle = {
  title: string;
  description: string;
  url: string;
  urlToImage?: string;
  publishedAt: string;
  source: { name?: string };
};

const fallbackNewsImage = 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&q=80&w=800';

export const NewsSection: React.FC = () => {
  const [news, setNews] = React.useState<NewsArticle[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch('/api/news');
        if (!response.ok) throw new Error('Failed to fetch news');
        const data = await response.json();
        const validArticles = Array.isArray(data.articles)
          ? data.articles.filter((article: NewsArticle) => article?.url && article?.title && article.title !== '[Removed]').slice(0, 3)
          : [];
        setNews(validArticles);
      } catch (err) {
        console.error("Error fetching news:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="text-center mb-16">
          <span className="text-[#009B8F] font-bold tracking-widest text-xs uppercase mb-2 block">Global Updates</span>
          <h2 className="text-4xl font-serif text-[#011E26]">Positive Study Abroad News</h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-200 h-96 rounded-lg"></div>
            ))}
          </div>
        ) : news.length === 0 ? (
          <p className="text-center text-slate-500" role="status">
            {error ? 'News updates are unavailable right now. Please check back soon.' : 'No positive study abroad updates are available right now. Please check back soon.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.map((article, index) => (
              <div key={index} className="group bg-white shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full rounded-lg overflow-hidden border border-gray-100">
                <div className="overflow-hidden h-56 relative">
                  <img
                    src={article.urlToImage || fallbackNewsImage}
                    alt={article.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackNewsImage; }}
                  />
                  <div className="absolute top-4 left-4 bg-[#011E26]/90 backdrop-blur-sm px-4 py-2 text-white text-[10px] font-bold tracking-widest uppercase rounded">
                    {article.source?.name || "News"}
                  </div>
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  <div className="text-[#009B8F] text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                    {new Date(article.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <h3 className="text-xl font-serif text-[#011E26] group-hover:text-[#009B8F] transition-colors leading-snug mb-4 line-clamp-3">
                    {article.title}
                  </h3>
                  <p className="text-slate-500 text-sm mb-6 line-clamp-3 flex-grow">
                    {article.description}
                  </p>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#011E26] text-sm font-bold flex items-center gap-2 group-hover:text-[#009B8F] transition-colors mt-auto"
                  >
                    Read Full Story <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const TestimonialsSection: React.FC = () => {
  return (
    <section className="py-24 bg-[#011E26] text-white relative overflow-hidden">

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="w-full md:w-1/3">
            <span className="text-[#009B8F] font-bold tracking-widest text-xs uppercase mb-2 block">Client Success</span>
            <h2 className="text-4xl lg:text-5xl font-serif mb-6">What Our Clients Say</h2>
            <p className="text-slate-400 leading-relaxed mb-8">
              We pride ourselves on our client-centric approach. Read about the experiences of those we've helped settle in the UK.
            </p>
            <button className="border border-[#009B8F] text-[#009B8F] px-8 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#009B8F] hover:text-white transition-colors">
              View All Reviews
            </button>
          </div>

          <div className="w-full md:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.slice(0, 2).map(t => (
              <div key={t.id} className="bg-[#022B36] p-8 border-t-4 border-[#009B8F] relative">
                <Quote className="absolute top-6 right-6 text-[#009B8F]/20" size={48} />
                <div className="flex gap-1 mb-6 text-[#009B8F]">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                </div>
                <p className="text-slate-300 mb-6 font-serif italic text-lg leading-relaxed">"{t.message}"</p>
                <div>
                  <h4 className="font-bold text-white tracking-wide uppercase text-sm">{t.name}</h4>
                  <p className="text-xs text-[#009B8F] uppercase tracking-wider mt-1">{t.visaType}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
